import { DeleteOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { DeletedFilled } from 'assets/images/svg';
import { useManagePermittedRoles } from 'library/hooks';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectBookingByBookingID } from 'reduxStore/bookings/selectors';
import { bookingActions } from 'reduxStore/bookings/slice';
import { selectDevicesByDeviceID } from 'reduxStore/devices/selectors';
import { selectPeopleByPersonID } from 'reduxStore/people/selectors';
import { Button, Divider, Flex, Modal, Space, Tooltip, Typography } from 'zui/Antd';

const { Text, Title } = Typography;

const DeleteBookingModal = ({ children, visible: visibleProp, setVisible: setVisibleProp, bookingID }) => {
	// Hooks
	const {
		token: { paddingLG, fontSizeSM, marginXL, marginXS, paddingXL, paddingXS, paddingSM }
	} = theme.useToken();
	const dispatch = useDispatch();

	// ✅ Redux state
	const booking = useSelector((state) => selectBookingByBookingID(state, bookingID));
	const device = useSelector((state) => selectDevicesByDeviceID(state, booking?.deviceID));
	const person = useSelector((state) => selectPeopleByPersonID(state, booking?.assignedToPerson));
	const { deleteBookingLoading, deleteBookingComplete } = useSelector((state) => state.Bookings);

	// ✅ Local state - Internal visible state
	const [internalVisible, setInternalVisible] = useState(false);
	const visible = visibleProp ?? internalVisible;
	const setVisible = setVisibleProp ?? setInternalVisible;

	// ✅ Permissions
	const canDeleteBooking = useManagePermittedRoles('deleteBooking', bookingID);
	const permissionDeniedMessage = "You don't have permission to perform this action.";

	const handleDelete = () => {
		dispatch(bookingActions.deleteBooking({ bookingID }));
	};

	const handleCancel = useCallback(() => setVisible(false), [setVisible]);

	useEffect(() => {
		if (deleteBookingComplete) {
			handleCancel();
			dispatch(bookingActions.clearDeleteBookingState());
		}
	}, [deleteBookingComplete, handleCancel, dispatch]);

	return (
		<>
			<Tooltip title={!canDeleteBooking ? permissionDeniedMessage : 'Delete this Booking'} placement='top'>
				<span onClick={(e) => canDeleteBooking && setVisible(true)}>
					{children ?? (
						<Button
							icon={<DeleteOutlined />}
							disabled={!canDeleteBooking || deleteBookingLoading}
							danger
							type='primary'
						>
							Delete
						</Button>
					)}
				</span>
			</Tooltip>

			<Modal
				open={visible}
				onCancel={handleCancel}
				closeIcon={false}
				centered
				destroyOnClose
				footer={[
					<Flex
						align='center'
						justify='center'
						style={{ marginTop: marginXL, padding: `0px ${paddingXS}px` }}
						gap={12}
						key='footer'
					>
						<Button onClick={handleCancel} type='default' disabled={deleteBookingLoading} block>
							Cancel
						</Button>
						<Button
							onClick={handleDelete}
							loading={deleteBookingLoading}
							disabled={deleteBookingLoading}
							type='primary'
							danger
							block
						>
							Delete
						</Button>
					</Flex>
				]}
				width={350}
				styles={{ content: { padding: paddingXL } }}
			>
				<Flex vertical align='center' justify='center' gap={paddingSM}>
					<DeletedFilled />
					<Flex vertical gap={paddingLG}>
						<Space
							size={paddingXS}
							direction='vertical'
							align='center'
							style={{ margin: `${paddingXS}px 0px` }}
						>
							<Title
								level={5}
								style={{
									margin: 0,
									lineHeight: '24px',
									textAlign: 'center',
									padding: `0px ${paddingXL}px`
								}}
							>
								Are you sure you want to delete this Booking?
							</Title>
							<Text
								style={{
									fontSize: fontSizeSM,
									lineHeight: '24px',
									textAlign: 'center',
									display: 'block'
								}}
							>
								This action is permanent and <br /> cannot be undone.
							</Text>
						</Space>

						<Flex vertical gap={marginXS}>
							<Divider orientation='center' plain style={{ margin: 0, padding: 0, marginTop: 0 }}>
								<Text style={{ fontSize: fontSizeSM, fontWeight: 200 }}>Selected Booking</Text>
							</Divider>
							<Text style={{ fontSize: fontSizeSM, fontWeight: 200, textAlign: 'center' }}>
								{device?.name} - {person?.displayName}
							</Text>
						</Flex>
					</Flex>
				</Flex>
			</Modal>
		</>
	);
};

export default DeleteBookingModal;
